import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Volume2, TreePine, RefreshCw, Trophy, Music, Star, Home } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// --- Game Constants and Assets ---
const ASSET_BASE_URL = "https://github.com/OmkarMore278/TalkyToon_Videos/releases/download/vs1";

const ANIMALS = [
    { id: 1, name: "Bird", soundFile: `${ASSET_BASE_URL}/Birds.mp3`, emoji: "🐦", color: "bg-blue-100" },
    { id: 2, name: "Frog", soundFile: `${ASSET_BASE_URL}/Frog.mp3`, emoji: "🐸", color: "bg-green-100" },
    { id: 3, name: "Owl", soundFile: `${ASSET_BASE_URL}/Owl.mp3`, emoji: "🦉", color: "bg-purple-100" },
    { id: 4, name: "Squirrel", soundFile: `${ASSET_BASE_URL}/Squirrel.mp3`, emoji: "🐿️", color: "bg-orange-100" },
    { id: 5, name: "Duck", soundFile: `${ASSET_BASE_URL}/Duck.mp3`, emoji: "🦆", color: "bg-yellow-100" },
    { id: 6, name: "Bee", soundFile: `${ASSET_BASE_URL}/Bee.mp3`, emoji: "🐝", color: "bg-amber-100" },
    { id: 7, name: "Cricket", soundFile: `${ASSET_BASE_URL}/Crickets.mp3`, emoji: "🦗", color: "bg-emerald-100" },
    { id: 8, name: "Wolf", soundFile: `${ASSET_BASE_URL}/Wolf.mp3`, emoji: "🐺", color: "bg-slate-100" },
];

const shuffleArray = (array) => {
    let newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
};

const WhisperWoodsSoundMatch = () => {
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [options, setOptions] = useState([]);
    const [score, setScore] = useState(0);
    const [message, setMessage] = useState("Tap the button to listen!");
    const [isLocked, setIsLocked] = useState(false);
    const [level, setLevel] = useState(1);
    
    const audioRef = useRef(null);

    const playSound = useCallback((src) => {
        if (!audioRef.current) audioRef.current = new Audio();
        audioRef.current.src = src;
        audioRef.current.play().catch(err => console.log("Audio play blocked", err));
    }, []);

    const setupNewRound = useCallback(() => {
        const shuffled = shuffleArray(ANIMALS);
        const newQuestion = shuffled[0];
        
        // Show 4 options at a time for better mobile focus
        const gameOptions = shuffleArray([
            newQuestion,
            ...shuffled.slice(1, 4)
        ]);

        setOptions(gameOptions);
        setCurrentQuestion(newQuestion);
        setMessage("Who is making that sound?");
        setIsLocked(false);
    }, []);

    useEffect(() => {
        setupNewRound();
    }, [setupNewRound]);

    const handlePlaySound = () => {
        if (currentQuestion && !isLocked) {
            playSound(currentQuestion.soundFile);
        }
    };

    const handleAnswerClick = (animal) => {
        if (isLocked) return;
        setIsLocked(true);

        if (animal.id === currentQuestion.id) {
            setMessage(`🌟 Yay! It's the ${animal.name}!`);
            setScore(prev => prev + 10);
            if (score > 0 && score % 50 === 0) setLevel(l => l + 1);
            
            setTimeout(() => {
                setupNewRound();
            }, 1800);
        } else {
            setMessage(`❌ That's the ${animal.name}. Listen again!`);
            setTimeout(() => {
                setIsLocked(false);
                setMessage("Try one more time!");
            }, 1200);
        }
    };

    return (
        <div className="min-h-screen bg-[#F0FDF4] p-4 md:p-8 font-['Fredoka',_sans-serif]">
            <style jsx global>{`
                @import url('https://fonts.googleapis.com/css2?family=Fredoka:wght@400;600;700&display=swap');
            `}</style>

            <main className="max-w-4xl mx-auto">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row md:justify-between items-center mb-6 gap-4">
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-green-600 rounded-2xl shadow-lg">
                            <TreePine className="text-white w-6 h-6" />
                        </div>
                        <div>
                            <h1 className="text-2xl md:text-3xl font-bold text-green-900 leading-none">Whisper Woods</h1>
                            <p className="text-green-700 text-sm font-medium">Sound Matching Adventure</p>
                        </div>
                    </div>
                    
                    <div className="flex gap-2">
                        <div className="bg-white px-4 py-2 rounded-2xl shadow-sm border-2 border-green-100 flex items-center gap-2">
                            <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                            <span className="font-bold text-green-800 text-lg">{score}</span>
                        </div>
                        <div className="bg-green-600 px-4 py-2 rounded-2xl shadow-md text-white flex items-center gap-2">
                            <Trophy className="w-5 h-5" />
                            <span className="font-bold text-sm">LVL {level}</span>
                        </div>
                    </div>
                </div>

                {/* Game Area */}
                <div className="bg-white rounded-[2.5rem] p-6 md:p-10 shadow-xl shadow-green-900/5 border-4 border-white relative overflow-hidden">
                    
                    {/* Feedback Message */}
                    <div className="text-center mb-8">
                        <div className={`inline-block px-6 py-2 rounded-full text-lg font-bold mb-4 ${
                            message.includes('🌟') ? 'bg-yellow-100 text-yellow-700' : 
                            message.includes('❌') ? 'bg-red-100 text-red-600' : 'bg-green-50 text-green-700'
                        }`}>
                            {message}
                        </div>
                    </div>

                    {/* Sound Button */}
{/* Sound Button Section */}
<div className="flex flex-col items-center mb-10">
    {/* Floating Instruction Label */}
    <motion.div 
        animate={{ y: [0, -5, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        className="mb-4 bg-indigo-600 text-white text-xs md:text-sm font-bold px-4 py-1.5 rounded-full shadow-lg flex items-center gap-2"
    >
        <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-100 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
        </span>
        CLICK TO PLAY SOUND
    </motion.div>

    <button
        onClick={handlePlaySound}
        disabled={isLocked}
        className={`group relative w-32 h-32 md:w-40 md:h-40 flex items-center justify-center rounded-full bg-gradient-to-br from-green-400 to-green-600 shadow-2xl shadow-green-200 active:scale-95 transition-all disabled:opacity-50
            ${!isLocked && "hover:from-green-500 hover:to-green-700"}
        `}
        aria-label="Click to hear animal sound"
    >
        {/* Animated Rings when hovering */}
        <div className="absolute inset-0 rounded-full bg-green-400 animate-ping opacity-20 group-hover:block hidden" />
        
        {/* Inner Pulse Circle */}
        <div className="absolute w-full h-full rounded-full border-4 border-white/30 scale-90 group-hover:scale-100 transition-transform duration-500" />
        
        <div className="flex flex-col items-center">
            <Volume2 className="w-14 h-14 md:w-16 md:h-16 text-white mb-1" />
            <span className="text-white/90 text-[10px] md:text-xs font-black tracking-widest">LISTEN</span>
        </div>
    </button>
</div>

                    {/* Options Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                        {options.map(animal => (
                            <button
                                key={animal.id}
                                onClick={() => handleAnswerClick(animal)}
                                disabled={isLocked}
                                className={`group relative p-4 md:p-6 rounded-3xl border-4 transition-all hover:-translate-y-1 active:scale-95 ${
                                    isLocked ? 'border-transparent grayscale opacity-50' : 'border-slate-50 hover:border-green-400 hover:shadow-lg'
                                } ${animal.color}`}
                            >
                                <span className="text-5xl md:text-6xl block mb-2">{animal.emoji}</span>
                                <span className="text-sm md:text-base font-bold text-slate-700 uppercase tracking-wide">{animal.name}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Footer Controls */}
                <div className="mt-8 flex justify-center gap-4">
                    <button 
                        onClick={() => { setScore(0); setLevel(1); setupNewRound(); }}
                        className="px-6 py-3 bg-white text-slate-600 font-bold rounded-2xl shadow-sm border border-slate-200 flex items-center gap-2 hover:bg-slate-50 transition-colors"
                    >
                        <RefreshCw className="w-5 h-5" />
                        Reset Game
                    </button>
                </div>
            </main>
        </div>
    );
};

export default WhisperWoodsSoundMatch;